import React from 'react';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import ConfigCard from './ConfigCard';
import { PdiRow as Row } from './_PdiHelpers';

/**
 * "Layout" card — controls how the header bar items are arranged.
 *
 * All 5 rows are settings-backed:
 *   menuStyle               ↔ HEADER_TYPE              ('menuRight' | 'menuBelow')
 *   actionButtonsPosition   ↔ HEADER_ACCOUNT_POSITION  ('start' | 'end')
 *                              — only meaningful when HEADER_TYPE === 'menuRight';
 *                              the dropdown disables itself otherwise.
 *   centerTopHeaderButtons  ↔ CENTER_HEADER
 *   centerTopButtonInMiddle ↔ CENTER_LOGO
 *   alignActionButtonsRight ↔ HEADER_FILTER_SPACE_ACTIONS
 */
const LayoutCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb = '') => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    // HEADER_TYPE — two storefront-supported values.
    const menuStyles = [
        { value: 'menuRight', label: tr('cfg_nav_layout_menu_style_right', 'Menu on the right of the logo') },
        { value: 'menuBelow', label: tr('cfg_nav_layout_menu_style_below', 'Menu below the header')         },
    ];
    // HEADER_ACCOUNT_POSITION — only meaningful when HEADER_TYPE === 'menuRight'.
    const actionPositions = [
        { value: 'start', label: tr('cfg_nav_layout_action_start', 'As the first / topmost item') },
        { value: 'end',   label: tr('cfg_nav_layout_action_end',   'As the last section in the list') },
    ];
    const menuStyleCurrent  = get('menuStyle', 'menuRight') || 'menuRight';
    const actionPosDisabled = menuStyleCurrent !== 'menuRight';

    return (
        <ConfigCard
            title={tr('cfg_nav_layout_title', 'Layout')}
            description={tr('cfg_nav_layout_desc', 'How items are arranged in the header.')}
            theme={theme}
        >
            <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
                {/* HEADER_TYPE. Width is fixed (not minWidth) so the long
                    option text doesn't stretch the dropdown — both Layout
                    selects share the same width for visual balance. The
                    dropdown menu itself still shows the full text. */}
                <Row
                    label={tr('cfg_nav_layout_menu_style', 'Menu style')}
                    help={tr('cfg_nav_layout_menu_style_help', 'Where the navigation menu sits relative to the logo')}
                    control={
                        <TextField
                            select
                            size="small"
                            value={menuStyleCurrent}
                            onChange={(e) => set('menuStyle')(e.target.value)}
                            sx={{
                                width: 260,
                                '& .MuiSelect-select': { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
                            }}
                        >
                            {menuStyles.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    }
                />

                {/* HEADER_ACCOUNT_POSITION — only meaningful when the menu
                    is on the right (matches the legacy admin's disabled
                    behaviour). */}
                <Row
                    label={tr('cfg_nav_layout_action_position', 'Action buttons position')}
                    help={tr('cfg_nav_layout_action_position_help', 'Where the account, cart, and other icon buttons appear')}
                    control={
                        <TextField
                            select
                            size="small"
                            value={get('actionButtonsPosition', 'end') || 'end'}
                            onChange={(e) => set('actionButtonsPosition')(e.target.value)}
                            sx={{
                                width: 260,
                                '& .MuiSelect-select': { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
                            }}
                            disabled={actionPosDisabled}
                            helperText={actionPosDisabled
                                ? tr('cfg_nav_layout_action_position_only_right', 'Only available when the menu is on the right of the logo')
                                : undefined}
                        >
                            {actionPositions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    }
                />

                {/* CENTER_HEADER — center the top row of header buttons. */}
                <Row
                    label={tr('cfg_nav_layout_center_header', 'Center all header items horizontally')}
                    help={tr('cfg_nav_layout_center_header_help', 'Aligns everything in the header as one centered group')}
                    control={
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Switch color="success"
                                    size="small"
                                    checked={!!get('centerTopHeaderButtons')}
                                    onChange={(e) => set('centerTopHeaderButtons')(e.target.checked)}
                                />
                            }
                            label=""
                        />
                    }
                />

                {/* CENTER_LOGO — center the logo in the middle of the header. */}
                <Row
                    label={tr('cfg_nav_layout_center_logo', 'Center the logo horizontally')}
                    help={tr('cfg_nav_layout_center_logo_help', 'Places the logo in the middle of the header')}
                    control={
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Switch color="success"
                                    size="small"
                                    checked={!!get('centerTopButtonInMiddle')}
                                    onChange={(e) => set('centerTopButtonInMiddle')(e.target.checked)}
                                />
                            }
                            label=""
                        />
                    }
                />

                {/* HEADER_FILTER_SPACE_ACTIONS — align action icons right. */}
                <Row
                    label={tr('cfg_nav_layout_align_action_right', 'Align action buttons to the right')}
                    help={tr('cfg_nav_layout_align_action_right_help', 'Pushes the account, cart, and other icon buttons to the far right of the menu bar')}
                    control={
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Switch color="success"
                                    size="small"
                                    checked={!!get('alignActionButtonsRight')}
                                    onChange={(e) => set('alignActionButtonsRight')(e.target.checked)}
                                />
                            }
                            label=""
                        />
                    }
                />
            </Stack>
        </ConfigCard>
    );
};

LayoutCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default LayoutCard;
